import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';
import {
    Camera,
    Gesture,
    lerpCamera,
    orbitCamera,
    panCamera,
    zoomCamera,
} from 'shared/components/Header/RoomPlanner/NavigationGuide/scene';

const WHEEL_SETTLE = 300;
const RETURN_DURATION = 900;
const WHEEL_ZOOM = 0.95;
const ORBIT_LOCK_DISTANCE = 6;
const TWO_FINGER_LOCK_DISTANCE = 12;

interface Point {
    x: number;
    y: number;
}

interface DioramaControlsOptions {
    getDemoCamera: () => Camera;
    autoReturn: boolean;
    onGesture: (gesture: Gesture, pointerType: string) => void;
}

const centre = (points: Point[]) => ({
    x: points.reduce((sum, p) => sum + p.x, 0) / points.length,
    y: points.reduce((sum, p) => sum + p.y, 0) / points.length,
});

const spread = ([a, b]: Point[]) => Math.hypot(a.x - b.x, a.y - b.y);

const easeInOut = (t: number) =>
    t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

export const useDioramaControls = ({
    getDemoCamera,
    autoReturn,
    onGesture,
}: DioramaControlsOptions) => {
    const svgRef = useRef<SVGSVGElement>(null);
    const [camera, setCamera] = useState<Camera | null>(null);
    const [dragging, setDragging] = useState(false);
    const [returning, setReturning] = useState(false);
    const cameraRef = useRef<Camera | null>(null);
    const pointers = useRef(new Map<number, Point>());
    const mode = useRef<'orbit' | 'pan' | null>(null);
    const last = useRef<{centre: Point; spread: number} | null>(null);
    const gestureStart = useRef<{centre: Point; spread: number} | null>(null);
    const lockedGesture = useRef<Gesture | null>(null);
    const idleTimer = useRef<number>();
    const returnFrame = useRef<number>();
    const latest = useRef({getDemoCamera, autoReturn, onGesture});

    latest.current = {getDemoCamera, autoReturn, onGesture};

    const update = useCallback((next: Camera | null) => {
        cameraRef.current = next;
        setCamera(next);
    }, []);

    const cancelReturn = useCallback(() => {
        window.clearTimeout(idleTimer.current);
        cancelAnimationFrame(returnFrame.current);
        setReturning(false);
    }, []);

    const easeBack = useCallback(() => {
        const from = cameraRef.current;

        if (!from) return;

        setReturning(true);

        const start = performance.now();
        const step = (now: number) => {
            const t = Math.min(1, (now - start) / RETURN_DURATION);

            if (t >= 1) {
                update(null);
                setReturning(false);
                return;
            }

            update(
                lerpCamera(from, latest.current.getDemoCamera(), easeInOut(t))
            );
            returnFrame.current = requestAnimationFrame(step);
        };

        returnFrame.current = requestAnimationFrame(step);
    }, [update]);

    const returnNow = useCallback(() => {
        cancelReturn();

        if (latest.current.autoReturn) {
            easeBack();
        }
    }, [cancelReturn, easeBack]);

    const returnAfterWheel = useCallback(() => {
        cancelReturn();

        if (latest.current.autoReturn) {
            idleTimer.current = window.setTimeout(easeBack, WHEEL_SETTLE);
        }
    }, [cancelReturn, easeBack]);

    useLayoutEffect(() => {
        if (autoReturn && cameraRef.current && !pointers.current.size) {
            returnNow();
        } else if (!autoReturn) {
            cancelReturn();
        }
    }, [autoReturn]);

    const apply = useCallback(
        (change: (current: Camera) => Camera) => {
            cancelReturn();
            update(change(cameraRef.current ?? latest.current.getDemoCamera()));
        },
        [cancelReturn, update]
    );

    const resetReference = () => {
        const points = Array.from(pointers.current.values());

        last.current = points.length
            ? {
                  centre: centre(points),
                  spread: points.length > 1 ? spread(points) : 0,
              }
            : null;
        gestureStart.current = last.current;
    };

    const detectTouchGesture = (points: Point[], now: Point) => {
        if (!gestureStart.current) return null;

        const travel = Math.hypot(
            now.x - gestureStart.current.centre.x,
            now.y - gestureStart.current.centre.y
        );

        if (points.length < 2) {
            return travel > ORBIT_LOCK_DISTANCE ? 'orbit' : null;
        }

        const spreadChange = Math.abs(
            spread(points) - gestureStart.current.spread
        );

        if (Math.max(travel, spreadChange) <= TWO_FINGER_LOCK_DISTANCE) {
            return null;
        }

        return spreadChange > travel ? 'zoom' : 'pan';
    };

    const lockGesture = (gesture: Gesture | null, pointerType: string) => {
        if (!gesture || gesture === lockedGesture.current) return;
        if (lockedGesture.current && lockedGesture.current !== 'orbit') return;

        lockedGesture.current = gesture;
        latest.current.onGesture(gesture, pointerType);
    };

    const onPointerDown = useCallback(
        (event: React.PointerEvent<SVGSVGElement>) => {
            if (event.pointerType === 'mouse') {
                if (event.button !== 0 && event.button !== 2) return;

                const panKey = event.shiftKey || event.ctrlKey || event.metaKey;
                mode.current = event.button === 2 || panKey ? 'pan' : 'orbit';
            }

            if (!pointers.current.size) {
                lockedGesture.current = null;
            }

            try {
                event.currentTarget.setPointerCapture(event.pointerId);
            } catch {}
            pointers.current.set(event.pointerId, {
                x: event.clientX,
                y: event.clientY,
            });
            resetReference();
            cancelReturn();
            setDragging(true);
        },
        [cancelReturn]
    );

    const onPointerMove = useCallback(
        (event: React.PointerEvent<SVGSVGElement>) => {
            if (!pointers.current.has(event.pointerId) || !last.current) return;

            pointers.current.set(event.pointerId, {
                x: event.clientX,
                y: event.clientY,
            });

            const points = Array.from(pointers.current.values());
            const now = centre(points);
            const deltaX = now.x - last.current.centre.x;
            const deltaY = now.y - last.current.centre.y;
            const {width, height} = event.currentTarget.getBoundingClientRect();
            const touch = event.pointerType !== 'mouse';

            if (touch && points.length > 1) {
                const distance = spread(points);
                const ratio = last.current.spread / distance;

                apply((current) =>
                    zoomCamera(panCamera(current, deltaX, deltaY, width), ratio)
                );
                lockGesture(detectTouchGesture(points, now), event.pointerType);
                last.current = {centre: now, spread: distance};
                return;
            }

            if (!deltaX && !deltaY) return;

            const gesture = touch ? 'orbit' : mode.current;

            apply((current) =>
                gesture === 'pan'
                    ? panCamera(current, deltaX, deltaY, width)
                    : orbitCamera(current, deltaX, deltaY, height)
            );
            lockGesture(
                touch ? detectTouchGesture(points, now) : gesture,
                event.pointerType
            );
            last.current = {centre: now, spread: 0};
        },
        [apply]
    );

    const onPointerUp = useCallback(
        (event: React.PointerEvent<SVGSVGElement>) => {
            if (!pointers.current.delete(event.pointerId)) return;

            resetReference();

            if (!pointers.current.size) {
                mode.current = null;
                setDragging(false);
                returnNow();
            }
        },
        [returnNow]
    );

    const onContextMenu = useCallback((event: React.MouseEvent) => {
        event.preventDefault();
    }, []);

    useLayoutEffect(() => {
        const svg = svgRef.current;

        if (!svg) return;

        const onWheel = (event: WheelEvent) => {
            event.preventDefault();

            if (!event.deltaY) return;

            apply((current) =>
                zoomCamera(
                    current,
                    event.deltaY > 0 ? 1 / WHEEL_ZOOM : WHEEL_ZOOM
                )
            );
            latest.current.onGesture('zoom', 'mouse');
            returnAfterWheel();
        };

        svg.addEventListener('wheel', onWheel, {passive: false});

        return () => svg.removeEventListener('wheel', onWheel);
    }, [apply, returnAfterWheel]);

    useEffect(() => cancelReturn, []);

    return {
        svgRef,
        camera,
        manual: camera !== null && !returning,
        dragging,
        handlers: {
            onPointerDown,
            onPointerMove,
            onPointerUp,
            onPointerCancel: onPointerUp,
            onContextMenu,
        },
    };
};
